/**
 * SitesIndexPage — list every site Opus knows about, drilling into one
 * opens the per-site overview at /sites/:slug.
 */
import { useEffect, useMemo, useReducer, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  AlertTriangle,
  ArrowRight,
  Building2,
  Check,
  ChevronLeft,
  ChevronRight,
  Copy,
  ExternalLink,
  KeyRound,
  Search,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardSub, CardTitle } from "@/components/ui/card";
import {
  CardGridSkeleton,
  PanelSkeleton,
  Skeleton,
} from "@/components/ui/skeleton";
import { useSites } from "@/stores/sites";
import type { SiteSummary } from "@/api/schemas/sites";
import { useAuth } from "@/stores/auth";
import { useUiAutoAnimate } from "@/components/ui/auto-animate";
import { normalizeHotelName } from "@/lib/hotel-name";

type SortKey = "code" | "onlineDevices" | "activeClients" | "org";

const SORT_LABELS: Record<SortKey, string> = {
  code: "Site code (A→Z)",
  onlineDevices: "WAN links (high→low)",
  activeClients: "Active clients (high→low)",
  org: "Org / tenant",
};

const PAGE_SIZE = 10;
type VendorFilter = "aruba" | "meraki" | "other";
type TestFilter = "test";
type FilterMode = "include" | "exclude" | "only";

const DEFAULT_FILTERS: Record<VendorFilter, FilterMode> = {
  aruba: "include",
  meraki: "include",
  other: "include",
};

const DEFAULT_TEST_FILTERS: Record<TestFilter, FilterMode> = {
  test: "include",
};

function siteMatchesVendorTag(
  siteVendor: string | undefined,
  tag: VendorFilter,
): boolean {
  const vendor = (siteVendor ?? "").toLowerCase();
  const hasMeraki = vendor.includes("meraki");
  const hasAruba = vendor.includes("aruba");

  if (tag === "meraki") return hasMeraki;
  if (tag === "aruba") return hasAruba;
  return !hasMeraki && !hasAruba;
}

function SitesIndexSkeleton() {
  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-center gap-2 text-xs text-fg-mute">
        <Skeleton className="h-3 w-20" />
        <span className="opacity-40">/</span>
        <Skeleton className="h-3 w-14" />
      </div>
      <div className="flex items-end justify-between gap-6">
        <div className="flex max-w-[680px] flex-1 flex-col gap-2">
          <Skeleton className="h-10 w-36" />
          <Skeleton className="h-4 w-[92%]" />
          <Skeleton className="h-4 w-[84%]" />
        </div>
        <Skeleton className="h-8 w-24 rounded-full" />
      </div>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <PanelSkeleton />
        <PanelSkeleton />
        <PanelSkeleton />
        <PanelSkeleton />
      </div>
      <Card>
        <CardContent className="flex flex-wrap items-center gap-3">
          <Skeleton className="h-10 min-w-[220px] flex-1 rounded-xl" />
          <Skeleton className="h-9 w-24 rounded-lg" />
          <Skeleton className="ml-auto h-4 w-28" />
        </CardContent>
      </Card>
      <CardGridSkeleton cards={4} />
    </div>
  );
}

function SitesNoKeysCard() {
  return (
    <Card>
      <CardContent className="flex items-center gap-4">
        <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-aurora-soft text-violet">
          <KeyRound size={20} />
        </div>
        <div className="flex-1">
          <div className="font-semibold text-fg">No sites visible yet</div>
          <div className="text-sm text-fg-dim">
            Sites come from your configured vendors. Add a Meraki key (org →
            networks become sites) or an Aruba bundle to populate this list.
          </div>
        </div>
        <div className="flex gap-2">
          <Button asChild size="sm" variant="ghost">
            <Link to="/aruba/keys">
              <KeyRound size={13} />
              Aruba bundle
            </Link>
          </Button>
          <Button asChild size="sm" variant="primary">
            <Link to="/meraki/keys">
              <KeyRound size={13} />
              Meraki key
            </Link>
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function SitesFilterBar({
  query,
  setQuery,
  sortBy,
  setSortBy,
  sitesTotal,
  filteredTotal,
  sitesListCachedAt,
  vendorFilters,
  vendorCounts,
  cycleVendorFilter,
  testFilters,
  cycleTestFilter,
}: {
  query: string;
  setQuery: (v: string) => void;
  sortBy: SortKey;
  setSortBy: (v: SortKey) => void;
  sitesTotal: number;
  filteredTotal: number;
  sitesListCachedAt: string | null;
  vendorFilters: Record<VendorFilter, FilterMode>;
  vendorCounts: Record<string, number>;
  cycleVendorFilter: (tag: VendorFilter) => void;
  testFilters: Record<TestFilter, FilterMode>;
  cycleTestFilter: (tag: TestFilter) => void;
}) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 30_000);
    return () => clearInterval(id);
  }, []);

  return (
    <Card>
      <CardContent className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[220px]">
          <Search
            size={14}
            className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-fg-mute"
          />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by code, friendly name, or org…"
            className="pl-9"
          />
        </div>
        <div className="flex items-center gap-2">
          <label
            htmlFor="sites-sort"
            className="font-mono text-[10px] uppercase tracking-[0.12em] text-fg-mute"
          >
            Sort
          </label>
          <select
            id="sites-sort"
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value as SortKey)}
            className="h-9 rounded-xl border border-line bg-surface px-3 text-sm text-fg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet/50"
          >
            {(Object.keys(SORT_LABELS) as SortKey[]).map((k) => (
              <option key={k} value={k}>
                {SORT_LABELS[k]}
              </option>
            ))}
          </select>
        </div>
        <div className="ml-auto flex flex-col items-end gap-0.5">
          <span className="font-mono text-[11px] text-fg-mute">
            {filteredTotal === sitesTotal
              ? `${sitesTotal} total`
              : `${filteredTotal} of ${sitesTotal} match`}
          </span>
          {sitesListCachedAt ? (
            <span className="font-mono text-[10px] text-fg-mute opacity-70">
              {`refreshed ${(() => {
                const ms = now - new Date(sitesListCachedAt).getTime();
                if (ms < 60_000) return `${Math.floor(ms / 1000)}s ago`;
                if (ms < 3_600_000) return `${Math.floor(ms / 60_000)}m ago`;
                return `${Math.floor(ms / 3_600_000)}h ago`;
              })()}`}
            </span>
          ) : null}
        </div>
        <div className="flex w-full flex-wrap items-center gap-2 pt-1">
          {(
            [
              ["meraki", "Meraki"],
              ["aruba", "Aruba"],
              ["other", "Other"],
            ] as [VendorFilter, string][]
          ).map(([key, label]) => {
            const mode = vendorFilters[key];
            const variant =
              mode === "exclude" ? "neutral" : mode === "only" ? "info" : "ok";
            return (
              <button
                key={key}
                type="button"
                onClick={() => cycleVendorFilter(key)}
                className="cursor-pointer"
                title={`${label}: click to cycle include -> only -> exclude`}
              >
                <Badge variant={variant} size="md">
                  {label} ({vendorCounts[key]})
                </Badge>
              </button>
            );
          })}
          {([["test", "Test"]] as [TestFilter, string][]).map(([key, label]) => {
            const mode = testFilters[key];
            const variant =
              mode === "exclude" ? "neutral" : mode === "only" ? "info" : "ok";
            return (
              <button
                key={key}
                type="button"
                onClick={() => cycleTestFilter(key)}
                className="cursor-pointer"
                title={`${label}: click to cycle include -> only -> exclude`}
              >
                <Badge variant={variant} size="md">
                  {label} ({vendorCounts[key]})
                </Badge>
              </button>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}

function SiteCard({
  site: s,
  copiedValue,
  onCopy,
  onOpenDashboard,
}: {
  site: SiteSummary;
  copiedValue: string | null;
  onCopy: (value: string, label: string) => void;
  onOpenDashboard: (url: string) => void;
}) {
  return (
    <Card>
      <CardContent className="flex flex-col gap-3">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <CardTitle className="truncate">
              {normalizeHotelName(s.friendlyName || s.code)} -{" "}
              {(s.innCode || s.code).toUpperCase()}
            </CardTitle>
            <CardSub className="mt-1 flex flex-wrap items-center gap-2 font-mono text-[11px] text-fg-mute">
              {s.navisionCode ? (
                <span className="inline-flex items-center gap-1.5">
                  <span>Navision:</span>
                  <span>{s.navisionCode}</span>
                  <button
                    type="button"
                    className="inline-flex h-5 w-5 items-center justify-center rounded-md border border-line text-fg-mute transition-colors hover:bg-surface-2 hover:text-fg"
                    onClick={() => onCopy(s.navisionCode as string, "Navision code")}
                    aria-label={`Copy navision code ${s.navisionCode}`}
                    title={`Copy navision code ${s.navisionCode}`}
                  >
                    {copiedValue === s.navisionCode ? (
                      <Check size={11} />
                    ) : (
                      <Copy size={11} />
                    )}
                  </button>
                </span>
              ) : null}
            </CardSub>
          </div>
          {s.uplinks.total > 0 ? (
            <Badge
              variant={s.uplinks.active === s.uplinks.total ? "ok" : "warn"}
              size="md"
            >
              {s.uplinks.active}/{s.uplinks.total} WAN
            </Badge>
          ) : null}
        </div>
        {s.activeClients > 0 ? (
          <div className="flex flex-wrap items-center gap-3 text-xs text-fg-dim">
            <span>
              <b className="font-mono text-fg">{s.activeClients}</b> active clients
            </span>
          </div>
        ) : null}
        <div className="flex items-center justify-between border-t border-line pt-3">
          <div className="text-[11px] text-fg-mute">{s.org}</div>
          <div className="flex items-center gap-2">
            {s.externalDashboardUrl ? (
              <Button
                size="sm"
                variant="ghost"
                onClick={() => onOpenDashboard(s.externalDashboardUrl as string)}
                title="Open this site in the vendor dashboard"
              >
                Open in Dashboard
                <ExternalLink size={13} />
              </Button>
            ) : null}
            <Button asChild size="sm">
              <Link to={`/sites/${s.slug}`}>
                Site Overview
                <ArrowRight size={13} />
              </Link>
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function SitesPagination({
  page,
  totalPages,
  totalItems,
  onPrev,
  onNext,
}: {
  page: number;
  totalPages: number;
  totalItems: number;
  onPrev: () => void;
  onNext: () => void;
}) {
  if (totalPages <= 1) return null;
  return (
    <div
      data-testid="sites-pagination"
      className="flex items-center justify-between text-sm text-fg-dim"
    >
      <span className="font-mono text-[11px]">
        Showing {(page - 1) * PAGE_SIZE + 1}–
        {Math.min(page * PAGE_SIZE, totalItems)} of {totalItems}
      </span>
      <div className="flex items-center gap-2">
        <Button size="sm" variant="ghost" onClick={onPrev} disabled={page <= 1}>
          <ChevronLeft size={13} />
          Prev
        </Button>
        <span className="font-mono text-[11px] text-fg">
          {page} / {totalPages}
        </span>
        <Button
          size="sm"
          variant="ghost"
          onClick={onNext}
          disabled={page >= totalPages}
        >
          Next
          <ChevronRight size={13} />
        </Button>
      </div>
    </div>
  );
}

function openExternalDashboard(url: string) {
  const ok = window.confirm(
    "Open this site in the vendor dashboard in a new tab?",
  );
  if (!ok) return;
  window.open(url, "_blank", "noopener,noreferrer");
}

export function SitesIndexPage() {
  const animatedRootRef = useUiAutoAnimate();
  const copyResetRef = useRef<number | null>(null);

  const { sites, status, error, sitesListCachedAt, loadSites } = useSites();
  const user = useAuth((s) => s.user);
  const [copiedValue, setCopiedValue] = useState<string | null>(null);
  const [vendorFilters, setVendorFilters] = useState(DEFAULT_FILTERS);
  const [testFilters, setTestFilters] = useState(DEFAULT_TEST_FILTERS);

  const includeOther =
    vendorFilters.other === "include" || vendorFilters.other === "only";
  const includeTest =
    testFilters.test === "include" || testFilters.test === "only";

  useEffect(() => {
    void loadSites({ includeOther, includeTest });
  }, [loadSites, includeOther, includeTest]);

  // No-keys empty state mirrors the /devices logic — a user with no
  // Meraki key gets a blank cards grid by default, which doesn't tell
  // them what to do next.
  const showNoKeysEmptyState =
    !!user &&
    user.merakiKeyConfigured === false &&
    sites.length === 0 &&
    !error &&
    status === "ready";

  // --- search / sort / paginate -----------------------------------------
  type SearchState = { query: string; sortBy: SortKey; page: number };
  const [{ query, sortBy, page }, setSearch] = useReducer(
    (s: SearchState, p: Partial<SearchState>) => ({ ...s, ...p }),
    { query: "", sortBy: "code", page: 1 },
  );
  // Wrappers that reset page on query/sort change — keeps prop signatures unchanged.
  const setQuery = (v: string) => setSearch({ query: v, page: 1 });
  const setSortBy = (v: SortKey) => setSearch({ sortBy: v, page: 1 });

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const onlyTag = (
      Object.entries(vendorFilters) as [VendorFilter, FilterMode][]
    ).find(([, mode]) => mode === "only")?.[0];

    return sites.filter((s) => {
      const includedByVendor = onlyTag
        ? siteMatchesVendorTag(s.vendor, onlyTag)
        : (Object.entries(vendorFilters) as [VendorFilter, FilterMode][])
            .filter(([, mode]) => mode !== "exclude")
            .some(([tag]) => siteMatchesVendorTag(s.vendor, tag));

      const includedByTest =
        testFilters.test === "only"
          ? Boolean(s.isTest)
          : testFilters.test === "exclude"
            ? !s.isTest
            : true;

      if (!includedByVendor || !includedByTest) return false;

      if (!q) return true;
      const hay =
        `${s.code} ${s.friendlyName} ${s.org} ${s.navisionCode ?? ""}`.toLowerCase();
      return hay.includes(q);
    });
  }, [sites, query, vendorFilters, testFilters]);

  const vendorCounts = useMemo(
    () => ({
      aruba: sites.filter((s) => siteMatchesVendorTag(s.vendor, "aruba"))
        .length,
      meraki: sites.filter((s) => siteMatchesVendorTag(s.vendor, "meraki"))
        .length,
      other: sites.filter((s) => siteMatchesVendorTag(s.vendor, "other"))
        .length,
      test: sites.filter((s) => Boolean(s.isTest)).length,
    }),
    [sites],
  );

  const sorted = useMemo(() => {
    // Always work on a copy — sort() mutates, and `filtered` is a memoized
    // reference some callers rely on. Cheap for the ~hundreds of sites
    // we'd reasonably display here.
    const arr = [...filtered];
    switch (sortBy) {
      case "code":
        arr.sort((a, b) => a.code.localeCompare(b.code));
        break;
      case "onlineDevices":
        // Prioritize more active WAN links, then by total known links.
        arr.sort((a, b) => {
          if (a.uplinks.active !== b.uplinks.active) {
            return b.uplinks.active - a.uplinks.active;
          }
          if (a.uplinks.total !== b.uplinks.total) {
            return b.uplinks.total - a.uplinks.total;
          }
          return a.code.localeCompare(b.code);
        });
        break;
      case "activeClients":
        arr.sort((a, b) => b.activeClients - a.activeClients);
        break;
      case "org":
        arr.sort(
          (a, b) => a.org.localeCompare(b.org) || a.code.localeCompare(b.code),
        );
        break;
    }
    return arr;
  }, [filtered, sortBy]);

  const totalPages = Math.max(1, Math.ceil(sorted.length / PAGE_SIZE));
  const visible = useMemo(
    () => sorted.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE),
    [sorted, page],
  );
  const isInitialLoading = status === "loading" && sites.length === 0;

  function copyFieldValue(value: string, label: string) {
    void navigator.clipboard.writeText(value).then(
      () => {
        setCopiedValue(value);
        toast.success(`Copied ${label} ${value}`);
        if (copyResetRef.current !== null) {
          window.clearTimeout(copyResetRef.current);
        }
        copyResetRef.current = window.setTimeout(
          () =>
            setCopiedValue((current) => (current === value ? null : current)),
          1600,
        );
      },
      () => toast.error(`Couldn't copy ${label}`),
    );
  }

  function cycleVendorFilter(tag: VendorFilter) {
    setVendorFilters((current) => {
      const currentMode = current[tag];
      if (currentMode === "include") {
        return {
          aruba: "exclude",
          meraki: "exclude",
          other: "exclude",
          [tag]: "only",
        };
      }
      if (currentMode === "only") {
        const next: Record<VendorFilter, FilterMode> = {
          ...current,
          aruba: tag === "aruba" ? "exclude" : "include",
          meraki: tag === "meraki" ? "exclude" : "include",
          other: tag === "other" ? "exclude" : "include",
        };
        return next;
      }
      return { ...current, [tag]: "include" };
    });
  }

  function cycleTestFilter(tag: TestFilter) {
    setTestFilters((current) => {
      const currentMode = current[tag];
      if (currentMode === "include") {
        return { [tag]: "only" };
      }
      if (currentMode === "only") {
        return { [tag]: "exclude" };
      }
      return { [tag]: "include" };
    });
  }

  if (isInitialLoading) {
    return <SitesIndexSkeleton />;
  }

  return (
    <div ref={animatedRootRef} className="flex flex-col gap-6">
      <div className="flex items-center gap-2 text-xs text-fg-mute">
        <Link to="/" className="text-fg-dim hover:text-fg">
          Operations
        </Link>
        <span className="opacity-40">/</span>
        <span className="text-fg">Sites</span>
      </div>

      <div className="flex items-end justify-between gap-6">
        <div>
          <h1 className="m-0 text-3xl font-extrabold tracking-tight text-aurora">
            Sites
          </h1>
          <p className="mt-1.5 max-w-[680px] text-sm text-fg-dim">
            Every site Opus can reach across all vendors. Click in for the live
            appliance status, topology, and connected clients overview.
          </p>
        </div>
        <Badge variant="info" size="md">
          <Building2 size={11} />
          {sites.length} sites
        </Badge>
      </div>

      {sites.length > 0 ? (
        <SitesFilterBar
          query={query}
          setQuery={setQuery}
          sortBy={sortBy}
          setSortBy={setSortBy}
          sitesTotal={sites.length}
          filteredTotal={sorted.length}
          sitesListCachedAt={sitesListCachedAt}
          vendorFilters={vendorFilters}
          vendorCounts={vendorCounts}
          cycleVendorFilter={cycleVendorFilter}
          testFilters={testFilters}
          cycleTestFilter={cycleTestFilter}
        />
      ) : null}

      {error ? (
        <output
          className="flex items-center gap-2.5 rounded-xl border border-peach/30 bg-peach/10 px-3.5 py-2.5 text-sm text-peach"
        >
          <AlertTriangle size={16} />
          <span>
            <b className="font-semibold">Showing fixture data.</b>{" "}
            <span className="text-peach/80">{error}</span>
          </span>
        </output>
      ) : null}

      {showNoKeysEmptyState ? <SitesNoKeysCard /> : null}

      {status === "loading" && sites.length === 0 ? (
        <CardGridSkeleton cards={4} />
      ) : sorted.length === 0 && sites.length > 0 ? (
        // Search returned no matches — distinguish from the genuine
        // "no sites at all" case (handled above by showNoKeysEmptyState
        // or by leaving the page blank when ready but empty).
        <Card>
          <CardContent className="py-10 text-center text-sm text-fg-mute">
            No sites match <span className="font-mono text-fg">"{query}"</span>.{" "}
            <button
              type="button"
              className="text-violet underline"
              onClick={() => setQuery("")}
            >
              Clear search
            </button>
          </CardContent>
        </Card>
      ) : (
        <>
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            {visible.map((s) => (
              <SiteCard
                key={s.slug}
                site={s}
                copiedValue={copiedValue}
                onCopy={copyFieldValue}
                onOpenDashboard={openExternalDashboard}
              />
            ))}
          </div>
          <SitesPagination
            page={page}
            totalPages={totalPages}
            totalItems={sorted.length}
            onPrev={() => setSearch({ page: Math.max(1, page - 1) })}
            onNext={() => setSearch({ page: Math.min(totalPages, page + 1) })}
          />
        </>
      )}
    </div>
  );
}
